const appearances = {
    loading: { label: 'Loading saved search', color: 'primary' },
    checking: { label: 'Checking live-search status', color: 'primary' },
    running: { label: 'Searching', color: 'primary' },
    queued: { label: 'Waiting to start', color: 'primary' },
    pending: { label: 'Awaiting update', color: 'primary' },
    recovering: { label: 'Recovering results', color: 'primary' },
    complete: { label: 'Complete', color: 'success' },
    warning: { label: 'Needs attention', color: 'warning' },
    unknown: { label: 'Unconfirmed', color: 'warning' },
    error: { label: 'Failed', color: 'danger' },
}

export const liveSearchAppearance = (status) => appearances[status] || appearances.unknown

export const liveSearchVendorName = (source) =>
    ({ ilsmart: 'ILSmart', boeing_api: 'Boeing', parts_base: 'PartsBase', textron: 'Textron' })[
        source
    ] || source.replace(/_/g, ' ')

export function describeLiveSearch({ runs = [], loading = false, resumeFailed = false } = {}) {
    const entries = []
    if (loading || resumeFailed) entries.push({ status: loading ? 'loading' : 'unknown' })
    runs.forEach((run) => {
        const vendors = run.vendors || []
        const healthy = vendors.length && vendors.every((v) => v.status === 'complete' && !v.failed)
        entries.push({
            status:
                !run.complete && !run.stopped
                    ? ['checking', 'loading', 'recovering'].includes(run.delivery)
                        ? run.delivery
                        : 'running'
                    : run.complete && healthy
                      ? 'complete'
                      : 'warning',
        })
        vendors.forEach((vendor) =>
            entries.push({
                ...vendor,
                status:
                    run.stopped && ['running', 'queued', 'pending'].includes(vendor.status)
                        ? 'unknown'
                        : vendor.status,
            })
        )
    })
    // Keep unfinished or failed work visible when a part edit created another run.
    const priority = ['running', 'queued', 'pending', 'error', 'unknown', 'warning', 'complete']
    const bySource = new Map()
    entries
        .filter((entry) => entry.source)
        .forEach((entry) => {
            const previous = bySource.get(entry.source)
            const status = appearances[entry.status] ? entry.status : 'unknown'
            const candidate = {
                ...entry,
                status,
                failed: Boolean(entry.failed || previous?.failed),
            }
            if (!previous || priority.indexOf(status) < priority.indexOf(previous.status)) {
                bySource.set(entry.source, candidate)
            } else if (entry.failed) {
                bySource.set(entry.source, { ...previous, failed: true })
            }
        })
    const vendors = [...bySource.values()]
    const statuses = entries.map((entry) => entry.status)
    let status =
        ['loading', 'recovering', 'checking', 'running', 'error', 'warning', 'unknown'].find(
            (candidate) => statuses.includes(candidate)
        ) || (entries.length ? 'complete' : 'unknown')
    if (status === 'error' && (resumeFailed || runs.some((run) => !run.complete)))
        status = 'unknown'
    let summary = liveSearchAppearance(status)
    const messages = {
        loading: "We're loading your saved search status.",
        recovering: "We're checking for results that haven't appeared yet.",
        checking: "We're checking whether your search has finished.",
        running:
            "We're still waiting for some vendors to finish. You can use the results already available.",
        error: "Some vendors couldn't complete the search. You can still use the results we found.",
        warning: "We couldn't confirm whether your search finished. Some results may be missing.",
        unknown: "We couldn't confirm whether your search finished. Some results may be missing.",
        complete: 'All vendors have finished. Your results are ready.',
    }
    if (status === 'running') {
        const active = vendors.filter((vendor) => vendor.status !== 'complete')
        const waiting = active.length && active.every((vendor) => vendor.status === 'queued')
        const issues = vendors.some(
            (vendor) => vendor.failed || ['error', 'unknown', 'warning'].includes(vendor.status)
        )
        const label = waiting
            ? 'Waiting for vendors'
            : runs.some((run) => run.resumed && !run.complete && run.delivery === 'polling')
              ? 'Live search still running'
              : 'Searching'
        summary = { ...summary, label: issues ? `${label} · needs attention` : label }
    } else if (status === 'error') {
        summary = { ...summary, label: 'Finished with errors' }
    } else if (status === 'unknown') {
        summary = liveSearchAppearance('warning')
    }
    return { entries, vendors, summary, message: messages[status] }
}
